import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCurrentUser,
  login,
  logout,
  register,
  createAdditionalOrganization,
  switchOrganizationPrimary,
  verifyEmail,
} from '../auth/authSlice';
import { getOrganizationSubscription } from '../../services/subscriptionService';
import {
  applyEnvelopeQuotaToSubscription,
  getOrganizationIdFromSubscription,
} from '../../utils/subscription';
import {
  clearAllRecipientContactsStorage,
  setActiveOrganizationId,
} from '../../utils/recipientContactsStore';

const applyUserSubscription = (state, subscription) => {
  if (subscription) {
    state.userSubscription = subscription;
    const orgId = getOrganizationIdFromSubscription(subscription);
    if (orgId) {
      setActiveOrganizationId(orgId);
    }
  }
};

export const fetchOrganizationSubscription = createAsyncThunk(
  'subscription/fetchOrganizationSubscription',
  async (_, { rejectWithValue }) => {
    try {
      return await getOrganizationSubscription();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to load billing details';
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  userSubscription: null,
  organizationSubscription: null,
  organizationStatus: 'idle',
  organizationError: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearOrganizationSubscriptionError(state) {
      state.organizationError = null;
    },
    syncEnvelopeQuotaFromApi(state, action) {
      const merged = applyEnvelopeQuotaToSubscription(
        state.userSubscription,
        action.payload
      );
      if (merged) {
        state.userSubscription = merged;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        applyUserSubscription(state, action.payload.subscription);
      })
      .addCase(register.fulfilled, (state, action) => {
        applyUserSubscription(state, action.payload.subscription);
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        applyUserSubscription(state, action.payload.subscription);
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        applyUserSubscription(state, action.payload.subscription);
      })
      .addCase(switchOrganizationPrimary.fulfilled, (state, action) => {
        applyUserSubscription(state, action.payload.subscription);
        state.organizationSubscription = null;
        state.organizationStatus = 'idle';
        state.organizationError = null;
      })
      .addCase(createAdditionalOrganization.fulfilled, (state, action) => {
        applyUserSubscription(state, action.payload.subscription);
        state.organizationSubscription = null;
        state.organizationStatus = 'idle';
        state.organizationError = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.userSubscription = null;
        state.organizationSubscription = null;
        state.organizationStatus = 'idle';
        state.organizationError = null;
        clearAllRecipientContactsStorage();
      })
      .addCase(logout.rejected, (state) => {
        state.userSubscription = null;
        state.organizationSubscription = null;
        state.organizationStatus = 'idle';
        state.organizationError = null;
        clearAllRecipientContactsStorage();
      })
      .addCase(fetchOrganizationSubscription.pending, (state) => {
        state.organizationStatus = 'loading';
        state.organizationError = null;
      })
      .addCase(fetchOrganizationSubscription.fulfilled, (state, action) => {
        state.organizationStatus = 'succeeded';
        state.organizationSubscription = action.payload;
      })
      .addCase(fetchOrganizationSubscription.rejected, (state, action) => {
        state.organizationStatus = 'failed';
        state.organizationError = action.payload || 'Failed to load billing details';
      });
  },
});

export const {
  clearOrganizationSubscriptionError,
  syncEnvelopeQuotaFromApi,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
